import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search, Grid, List, Sparkles, Play, ArrowRight, Plus, X, UserPlus,
  Loader2, ExternalLink, ChevronLeft, ChevronRight
} from 'lucide-react';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { fetchGalleryItems, type GalleryItem } from '@/lib/cms';
import BreadcrumbJsonLd from '@/components/BreadcrumbJsonLd';

const GRADIENT_PALETTES = [
  'from-[#635bff] to-[#00d4ff]',
  'from-[#00d4ff] to-[#00ff88]',
  'from-[#a855f7] to-[#ec4899]',
  'from-[#ff6b6b] to-[#ffa500]',
  'from-[#635bff] to-[#a855f7]',
  'from-[#00d4ff] to-[#635bff]',
];

const SELECT_CLASS = 'pl-4 pr-10 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#635bff]/50 appearance-none bg-[length:16px_16px] bg-[right_12px_center] bg-no-repeat bg-[url("data:image/svg+xml,%3Csvg%20xmlns%3D%27http%3A//www.w3.org/2000/svg%27%20fill%3D%27none%27%20viewBox%3D%270%200%2024%2024%27%20stroke%3D%27%2394a3b8%27%20stroke-width%3D%272%27%3E%3Cpath%20d%3D%27M19%209l-7%207-7-7%27/%3E%3C/svg%3E")]';

const FEATURED_COUNT = 3;
const GRID_COLS = 4;
// Initial load: featured row + 2 grid rows
const INITIAL_GRID = GRID_COLS * 2; // 8
const INITIAL_TOTAL = FEATURED_COUNT + INITIAL_GRID; // 11
const LOAD_MORE_COUNT = 8;

/** Build sorted dropdown options from a list of items, extracting unique values via accessor. */
function buildOptions(items: GalleryItem[], accessor: (i: GalleryItem) => string | string[], allLabel: string) {
  const values = new Set<string>();
  for (const item of items) {
    const v = accessor(item);
    if (Array.isArray(v)) v.forEach(x => { if (x) values.add(x); });
    else if (v) values.add(v);
  }
  return [
    { id: 'all', label: allLabel },
    ...[...values].sort().map(v => ({ id: v, label: v })),
  ];
}

/** Render a single gallery card */
function GalleryCard({ item, idx, featured = false, onImport, onViewDemo }: { item: GalleryItem; idx: number; featured?: boolean; onImport?: (item: GalleryItem) => void; onViewDemo?: (item: GalleryItem) => void }) {
  const showImport = item.allow_console_import && !!item.console_import_url;
  return (
    <div
      className="group bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden hover:border-[#635bff]/30 transition-all cursor-pointer"
      onClick={() => { if (onViewDemo) onViewDemo(item); }}
    >
      <div className={`relative ${featured ? 'aspect-[16/9]' : 'aspect-[4/3]'} overflow-hidden`}>
        {item.featured_image ? (
          <img
            src={item.featured_image}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            style={{ imageRendering: '-webkit-optimize-contrast' as React.CSSProperties['imageRendering'] }}
            loading={featured ? 'eager' : 'lazy'}
            decoding="async"
          />
        ) : (
          <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-4`}>
            <span className={`text-white/90 font-semibold text-center line-clamp-3 ${featured ? 'text-lg' : 'text-sm'}`}>{item.title}</span>
          </div>
        )}

        {/* Hover overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
            <div className={`absolute bottom-4 left-4 right-4 flex ${featured ? 'flex-nowrap' : 'flex-wrap'} ${showImport ? 'justify-center gap-2' : 'justify-center'}`}>
              <button
                onClick={(e) => { e.stopPropagation(); if (onViewDemo) onViewDemo(item); }}
                className={`inline-flex items-center justify-center gap-1.5 py-2 bg-[#635bff] text-white font-medium rounded-lg hover:bg-[#5449e6] transition-colors ${featured ? 'flex-1 px-3 text-sm whitespace-nowrap' : showImport ? 'flex-1 min-w-[140px] px-6' : 'px-6'}`}
              >
                <Play className="w-4 h-4 flex-shrink-0" />
                View Demo
              </button>
              {showImport && (
                <button
                  onClick={(e) => { e.stopPropagation(); onImport?.(item); }}
                  className={`inline-flex items-center justify-center gap-1.5 py-2 bg-white/20 backdrop-blur-sm text-white font-medium rounded-lg hover:bg-white/30 transition-colors ${featured ? 'flex-1 px-3 text-sm whitespace-nowrap' : 'flex-1 min-w-[140px] px-4'}`}
                >
                  <Plus className="w-4 h-4 flex-shrink-0" />
                  Add to Account
                </button>
              )}
            </div>
          </div>

        {/* Featured badge */}
        {featured && (
          <span className="absolute top-4 left-4 px-3 py-1 text-xs font-medium text-white bg-[#635bff] rounded-full">
            Featured
          </span>
        )}

        {/* Format Badge */}
        {item.ad_format && (
          <div className={`absolute ${featured ? 'top-4 right-4' : 'top-3 left-3'}`}>
            <span className="px-2 py-1 text-xs font-medium text-white bg-black/50 backdrop-blur-sm rounded-full">
              {item.ad_format}
            </span>
          </div>
        )}
      </div>

      <div className={featured ? 'p-6' : 'p-4'}>
        <h3 className={`font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors ${featured ? 'text-xl' : ''}`}>
          {item.title}
        </h3>
        {(item.excerpt || item.description) && (
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">
            {item.excerpt || item.description}
          </p>
        )}
        {item.industry.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1">
            {item.industry.slice(0, featured ? 3 : 2).map(ind => (
              <span key={ind} className="px-2 py-0.5 text-xs text-slate-600 dark:text-slate-400 bg-slate-100 dark:bg-white/5 rounded">
                {ind}
              </span>
            ))}
            {item.industry.length > (featured ? 3 : 2) && (
              <span className="px-2 py-0.5 text-xs text-slate-500 dark:text-slate-500">
                +{item.industry.length - (featured ? 3 : 2)}
              </span>
            )}
          </div>
        )}
        {featured && (
          <div className="mt-4 flex items-center justify-end">
            <ArrowRight className="w-5 h-5 text-[#635bff] transition-transform group-hover:translate-x-1" />
          </div>
        )}
      </div>
    </div>
  );
}

const Gallery: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Initialise filter state from URL params so shared/bookmarked URLs pre-filter the gallery
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') ?? '');
  const [selectedFormat, setSelectedFormat] = useState(() => searchParams.get('format') ?? 'all');
  const [selectedIndustry, setSelectedIndustry] = useState(() => searchParams.get('industry') ?? 'all');
  const [selectedComponent, setSelectedComponent] = useState(() => searchParams.get('component') ?? 'all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');

  // Keep URL in sync with filter state; use replace so filters don't pollute browser history.
  // Search is debounced 300ms so typing doesn't create a URL update per keystroke.
  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(() => {
    const apply = (q: string) => {
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        q ? next.set('search', q) : next.delete('search');
        selectedFormat !== 'all' ? next.set('format', selectedFormat) : next.delete('format');
        selectedIndustry !== 'all' ? next.set('industry', selectedIndustry) : next.delete('industry');
        selectedComponent !== 'all' ? next.set('component', selectedComponent) : next.delete('component');
        return next;
      }, { replace: true });
    };

    if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current);
    searchDebounceRef.current = setTimeout(() => apply(searchQuery), 300);
    return () => { if (searchDebounceRef.current) clearTimeout(searchDebounceRef.current); };
  }, [searchQuery, selectedFormat, selectedIndustry, selectedComponent]); // eslint-disable-line react-hooks/exhaustive-deps
  const [galleryItems, setGalleryItems] = useState<GalleryItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [importItem, setImportItem] = useState<GalleryItem | null>(null);
  const [visibleCount, setVisibleCount] = useState(INITIAL_TOTAL);
  // Sentinel ref for infinite scroll
  const sentinelRef = useRef<HTMLDivElement>(null);
  const loadingMoreRef = useRef(false);

  const handleViewDemo = (item: GalleryItem) => {
    const url = `/gallery/preview?item=${encodeURIComponent(item.slug)}&fullscreen=true`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  useEffect(() => {
    let cancelled = false;

    async function loadGallery() {
      try {
        setLoading(true);
        setError(null);
        const items = await fetchGalleryItems();
        if (cancelled) return;
        setGalleryItems(items);
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to load gallery');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    loadGallery();
    return () => { cancelled = true; };
  }, []);

  // Derive filter options from CMS data
  const formatOptions = useMemo(() => buildOptions(galleryItems, i => i.ad_format, 'All Formats'), [galleryItems]);
  const industryOptions = useMemo(() => buildOptions(galleryItems, i => i.industry, 'All Industries'), [galleryItems]);
  const componentOptions = useMemo(() => buildOptions(galleryItems, i => i.included_components, 'All Components'), [galleryItems]);

  const hasActiveFilters = searchQuery !== '' || selectedFormat !== 'all' || selectedIndustry !== 'all' || selectedComponent !== 'all';

  const filteredItems = useMemo(() => {
    return galleryItems.filter(item => {
      const query = searchQuery.toLowerCase();
      const matchesSearch = !searchQuery ||
        item.title.toLowerCase().includes(query) ||
        (item.excerpt || item.description).toLowerCase().includes(query) ||
        item.ad_format.toLowerCase().includes(query) ||
        item.industry.some(v => v.toLowerCase().includes(query));
      const matchesFormat = selectedFormat === 'all' || item.ad_format === selectedFormat;
      const matchesIndustry = selectedIndustry === 'all' || item.industry.includes(selectedIndustry);
      const matchesComponent = selectedComponent === 'all' || item.included_components.includes(selectedComponent);
      return matchesSearch && matchesFormat && matchesIndustry && matchesComponent;
    });
  }, [galleryItems, searchQuery, selectedFormat, selectedIndustry, selectedComponent]);

  // Reset visible count when filters change
  useEffect(() => {
    setVisibleCount(INITIAL_TOTAL);
  }, [searchQuery, selectedFormat, selectedIndustry, selectedComponent, viewMode]);

  const isFirstPage = !hasActiveFilters && viewMode === 'grid';
  const showFeatured = isFirstPage;

  // Items currently shown
  const visibleItems = filteredItems.slice(0, visibleCount);
  const hasMore = visibleCount < filteredItems.length;

  // Determine featured vs grid split
  const { featuredPosts, gridPosts } = useMemo(() => {
    if (!showFeatured) {
      return { featuredPosts: [] as GalleryItem[], gridPosts: visibleItems };
    }
    return {
      featuredPosts: visibleItems.slice(0, FEATURED_COUNT),
      gridPosts: visibleItems.slice(FEATURED_COUNT),
    };
  }, [visibleItems, showFeatured]);

  // Infinite scroll: load more when sentinel is ≥50% visible
  const loadMore = useCallback(() => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setVisibleCount(c => c + LOAD_MORE_COUNT);
    // Small delay to debounce rapid triggers
    setTimeout(() => { loadingMoreRef.current = false; }, 200);
  }, [hasMore]);

  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loadMore]);

  function clearFilters() {
    setSearchQuery('');
    setSelectedFormat('all');
    setSelectedIndustry('all');
    setSelectedComponent('all');
    setSearchParams({}, { replace: true });
  }

  // List view: simple pagination (keep for list mode)
  const LIST_PER_PAGE = 8;
  const [listPage, setListPage] = useState(1);
  useEffect(() => { setListPage(1); }, [searchQuery, selectedFormat, selectedIndustry, selectedComponent]);
  const totalListPages = Math.ceil(filteredItems.length / LIST_PER_PAGE) || 1;
  const listItems = filteredItems.slice((listPage - 1) * LIST_PER_PAGE, listPage * LIST_PER_PAGE);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0a0a0f]">
      <BreadcrumbJsonLd crumbs={[
        { name: 'Home', item: '/' },
        { name: 'Gallery' },
      ]} />
      <Header />

      {/* Hero */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-[#635bff]/20 via-transparent to-[#00d4ff]/10" />
        <div className="absolute top-20 left-1/4 w-96 h-96 bg-[#635bff]/20 rounded-full blur-3xl animate-float" />
        <div className="absolute bottom-0 right-1/4 w-80 h-80 bg-[#00d4ff]/15 rounded-full blur-3xl animate-float-delayed" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="max-w-4xl">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 text-sm font-medium text-[#635bff] bg-[#635bff]/10 border border-[#635bff]/20 rounded-full mb-6">
              <Sparkles className="w-4 h-4" />
              Interactive Ad Gallery
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold text-slate-900 dark:text-white leading-tight">
              Ad Gallery
            </h1>
            <p className="mt-6 text-xl text-slate-600 dark:text-slate-400">
              Explore interactive ad formats that drive real engagement and results.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-6 border-y border-slate-200 dark:border-white/10 sticky top-16 lg:top-20 bg-white/95 dark:bg-[#0a0a0f]/95 backdrop-blur-sm z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Single row: search (1/4) + format + industry + components, then counter + clear + view toggle */}
          <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:gap-3">
            {/* 4-column filter row */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 flex-1">
              {/* Search — 1/4 width on large screens */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500 dark:text-slate-400" aria-hidden="true" />
                <label htmlFor="gallery-search" className="sr-only">Search gallery</label>
                <input
                  id="gallery-search"
                  type="text"
                  placeholder="Search gallery..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 rounded-lg text-slate-900 dark:text-white placeholder-slate-500 dark:placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-[#635bff]/50"
                />
              </div>

              <label htmlFor="gallery-format" className="sr-only">Filter by format</label>
              <select id="gallery-format" value={selectedFormat} onChange={(e) => setSelectedFormat(e.target.value)} className={`w-full ${SELECT_CLASS}`}>
                {formatOptions.map(opt => (
                  <option key={opt.id} value={opt.id} className="bg-white dark:bg-[#1a1a2e]">{opt.label}</option>
                ))}
              </select>

              <label htmlFor="gallery-industry" className="sr-only">Filter by industry</label>
              <select id="gallery-industry" value={selectedIndustry} onChange={(e) => setSelectedIndustry(e.target.value)} className={`w-full ${SELECT_CLASS}`}>
                {industryOptions.map(opt => (
                  <option key={opt.id} value={opt.id} className="bg-white dark:bg-[#1a1a2e]">{opt.label}</option>
                ))}
              </select>

              <label htmlFor="gallery-component" className="sr-only">Filter by component</label>
              <select id="gallery-component" value={selectedComponent} onChange={(e) => setSelectedComponent(e.target.value)} className={`w-full ${SELECT_CLASS}`}>
                {componentOptions.map(opt => (
                  <option key={opt.id} value={opt.id} className="bg-white dark:bg-[#1a1a2e]">{opt.label}</option>
                ))}
              </select>
            </div>

            {/* Right side: counter + clear + view toggle */}
            <div className="flex items-center gap-3 lg:flex-shrink-0">
              <span className="text-sm text-slate-600 dark:text-slate-400 whitespace-nowrap">
                {filteredItems.length} of {galleryItems.length} items
              </span>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="inline-flex items-center gap-1 text-sm text-[#635bff] hover:text-[#5449e6] transition-colors whitespace-nowrap"
                >
                  <X className="w-3.5 h-3.5" />
                  Clear filters
                </button>
              )}
              <div className="flex bg-slate-100 dark:bg-white/5 rounded-lg p-1" role="group" aria-label="View mode">
                <button
                  onClick={() => setViewMode('grid')}
                  aria-label="Grid view"
                  aria-pressed={viewMode === 'grid'}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'grid' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <Grid className="w-4 h-4" aria-hidden="true" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  aria-label="List view"
                  aria-pressed={viewMode === 'list'}
                  className={`p-2 rounded-md transition-colors ${viewMode === 'list' ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'}`}
                >
                  <List className="w-4 h-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Content */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="flex items-center justify-center py-32">
              <Loader2 className="w-8 h-8 text-[#635bff] animate-spin" />
              <span className="ml-3 text-slate-600 dark:text-slate-400">Loading gallery...</span>
            </div>
          ) : error ? (
            <div className="text-center py-32">
              <p className="text-red-500 mb-4">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-[#635bff] text-white rounded-lg hover:bg-[#635bff]/90 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-32">
              <p className="text-slate-600 dark:text-slate-400">No gallery items match your query.</p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="mt-4 px-4 py-2 text-[#635bff] border border-[#635bff]/30 rounded-lg hover:bg-[#635bff]/10 transition-colors"
                >
                  Clear all filters
                </button>
              )}
            </div>
          ) : viewMode === 'list' ? (
            /* List View */
            <>
              <div className="space-y-4">
                {listItems.map((item, idx) => (
                  <div
                    key={item.id}
                    className="group flex items-center gap-6 p-4 bg-slate-50 dark:bg-white/5 backdrop-blur-sm border border-slate-200 dark:border-white/10 rounded-xl hover:border-[#635bff]/30 transition-all"
                  >
                    <div className="w-32 h-24 rounded-lg overflow-hidden flex-shrink-0">
                      {item.featured_image ? (
                        <img src={item.featured_image} alt={item.title} className="w-full h-full object-cover" loading="lazy" />
                      ) : (
                        <div className={`w-full h-full bg-gradient-to-br ${GRADIENT_PALETTES[idx % GRADIENT_PALETTES.length]} flex items-center justify-center p-2`}>
                          <span className="text-white/90 text-xs font-semibold text-center line-clamp-2">{item.title}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 dark:text-white group-hover:text-[#635bff] transition-colors">{item.title}</h3>
                      {(item.excerpt || item.description) && (
                        <p className="mt-1 text-sm text-slate-600 dark:text-slate-400 line-clamp-2">{item.excerpt || item.description}</p>
                      )}
                      <div className="mt-2 flex flex-wrap items-center gap-3 text-sm text-slate-500 dark:text-slate-400">
                        {item.ad_format && <span>{item.ad_format}</span>}
                        {item.industry.length > 0 && (
                          <span className="text-slate-400 dark:text-slate-500">{item.industry.join(', ')}</span>
                        )}
                      </div>
                    </div>
                    <div className="flex-shrink-0 flex flex-col gap-2 w-44">
                      {item.demo_url && (
                        <button
                          onClick={() => handleViewDemo(item)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-white bg-[#635bff] rounded-lg hover:bg-[#5449e6] transition-colors">
                          <ExternalLink className="w-4 h-4" />
                          View Demo
                        </button>
                      )}
                      {item.allow_console_import && item.console_import_url && (
                        <button
                          onClick={() => setImportItem(item)}
                          className="w-full inline-flex items-center justify-center gap-2 px-4 py-2 text-[#635bff] border border-[#635bff]/30 rounded-lg hover:bg-[#635bff]/10 transition-colors text-sm font-medium"
                        >
                          <Plus className="w-4 h-4" />
                          Add to Account
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* List pagination */}
              {totalListPages > 1 && (
                <div className="mt-12 flex items-center justify-center gap-2">
                  <button
                    onClick={() => setListPage(p => Math.max(1, p - 1))}
                    disabled={listPage === 1}
                    className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  {Array.from({ length: Math.min(5, totalListPages) }, (_, i) => {
                    const half = 2;
                    let start = Math.max(1, listPage - half);
                    let end = start + 4;
                    if (end > totalListPages) { end = totalListPages; start = Math.max(1, end - 4); }
                    return start + i;
                  }).map(page => (
                    <button
                      key={page}
                      onClick={() => setListPage(page)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors ${listPage === page ? 'bg-[#635bff] text-white' : 'text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10'}`}
                    >
                      {page}
                    </button>
                  ))}
                  <button
                    onClick={() => setListPage(p => Math.min(totalListPages, p + 1))}
                    disabled={listPage === totalListPages}
                    className="p-2 rounded-lg text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/10 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              )}
            </>
          ) : (
            /* Grid View — infinite scroll */
            <>
              {/* Featured row — only when no active filters */}
              {showFeatured && featuredPosts.length > 0 && (
                <div className="grid md:grid-cols-3 gap-6 mb-12">
                  {featuredPosts.map((item, idx) => (
                    <GalleryCard key={item.id} item={item} idx={idx} featured onImport={setImportItem} onViewDemo={handleViewDemo} />
                  ))}
                </div>
              )}

              {/* Regular grid — 4 columns */}
              {gridPosts.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {gridPosts.map((item, idx) => (
                    <GalleryCard key={item.id} item={item} idx={idx + (showFeatured ? FEATURED_COUNT : 0)} onImport={setImportItem} onViewDemo={handleViewDemo} />
                  ))}
                </div>
              )}

              {/* Infinite scroll sentinel — placed after 2nd-to-last row to trigger early */}
              {hasMore && (
                <div ref={sentinelRef} className="mt-8 flex justify-center py-4">
                  <Loader2 className="w-6 h-6 text-[#635bff]/50 animate-spin" />
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-900 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white">Ready to create your own?</h2>
          <p className="mt-4 text-lg text-slate-300 dark:text-slate-400">
            See how Adventive can help you build interactive ads that perform.
          </p>
          <button
            onClick={() => navigate('/demo')}
            className="mt-8 inline-flex items-center px-8 py-4 text-base font-semibold text-white bg-[#635bff] rounded-lg hover:bg-[#5449e6] transition-colors"
          >
            Request Demo
            <ArrowRight className="ml-2 h-5 w-5" />
          </button>
        </div>
      </section>

      {/* Import to Account Modal */}
      {importItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setImportItem(null)} aria-hidden="true">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" />
          <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="import-modal-title"
            className="relative w-full max-w-md bg-white dark:bg-[#1a1a2e] rounded-2xl shadow-2xl overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close button */}
            <button
              onClick={() => setImportItem(null)}
              aria-label="Close import dialog"
              className="absolute top-4 right-4 z-10 p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-white bg-white/80 dark:bg-black/40 backdrop-blur-sm rounded-full transition-colors"
            >
              <X className="w-4 h-4" aria-hidden="true" />
            </button>

            {/* Featured image */}
            {importItem.featured_image ? (
              <img
                src={importItem.featured_image}
                alt={importItem.title}
                className="w-full aspect-[16/9] object-cover"
              />
            ) : (
              <div className="w-full aspect-[16/9] bg-gradient-to-br from-[#635bff] to-[#00d4ff] flex items-center justify-center p-6">
                <span className="text-white/90 text-xl font-semibold text-center">{importItem.title}</span>
              </div>
            )}

            {/* Content */}
            <div className="p-6">
              <h3 id="import-modal-title" className="text-xl font-bold text-slate-900 dark:text-white">{importItem.title}</h3>
              {importItem.ad_format && (
                <span className="inline-block mt-2 px-2.5 py-0.5 text-xs font-medium text-[#635bff] bg-[#635bff]/10 rounded-full">
                  {importItem.ad_format}
                </span>
              )}
              <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
                Add this unit to your Adventive account? You'll be redirected to the Adventive Console to complete the import.
              </p>

              {/* Action buttons */}
              <div className="mt-6 flex flex-col gap-3">
                <button
                  onClick={() => {
                    window.open(importItem.console_import_url, '_blank', 'noopener,noreferrer');
                    setImportItem(null);
                  }}
                  className="w-full inline-flex items-center justify-center gap-2 py-3 px-6 bg-[#635bff] text-white font-semibold rounded-lg hover:bg-[#5449e6] transition-colors"
                >
                  <Plus className="w-5 h-5" />
                  Add to My Account
                </button>
                <button
                  onClick={() => setImportItem(null)}
                  className="w-full py-2.5 px-6 text-sm font-medium text-slate-600 dark:text-slate-400 border border-slate-200 dark:border-white/10 rounded-lg hover:bg-slate-50 dark:hover:bg-white/5 transition-colors"
                >
                  Cancel
                </button>
              </div>

              {/* Signup CTA */}
              <div className="mt-5 pt-5 border-t border-slate-200 dark:border-white/10 text-center">
                <p className="text-sm text-slate-500 dark:text-slate-400">Don't have an account?</p>
                <a
                  href="https://console.adventive.com/auth/register"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-2 inline-flex items-center gap-2 text-sm font-semibold text-[#635bff] hover:text-[#5449e6] transition-colors"
                >
                  <UserPlus className="w-4 h-4" />
                  Sign up for free
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default Gallery;
